import { useEffect, useState } from 'react'
import { Link2, RefreshCw, Search, Loader2, X, Users, Unlink } from 'lucide-react'
import { relationsApi, studentsApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const getPersonName = (u) => {
  if (!u) return '—'
  return u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'
}

const getClassLabel = (student) => {
  const classItem = student?.classId || student?.class
  if (!classItem) return student?.className || 'No class'
  const name = classItem.name || student?.className || 'Class'
  return classItem.section ? `${name} — ${classItem.section}` : name
}

export default function ManageRelationsPage() {
  const [unlinked, setUnlinked] = useState([])
  const [parents, setParents] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [linkModal, setLinkModal] = useState(null) // student object
  const [linkForm, setLinkForm] = useState({ parentId: '', relation: 'father' })
  const [linking, setLinking] = useState(false)
  const [linkedStudents, setLinkedStudents] = useState([]) // with parents loaded
  const [loadingLinked, setLoadingLinked] = useState(false)

  const relations = ['father', 'mother', 'guardian']

  const fetchData = async () => {
    try {
      setLoading(true)
      const [unlinkedRes, parentsRes] = await Promise.allSettled([
        relationsApi.getUnlinkedStudents(),
        studentsApi.getAll({ role: 'parent' }), // parents are users with role=parent
      ])
      if (unlinkedRes.status === 'fulfilled') setUnlinked(Array.isArray(unlinkedRes.value) ? unlinkedRes.value : unlinkedRes.value?.students ?? [])
      if (parentsRes.status === 'fulfilled') setParents(Array.isArray(parentsRes.value) ? parentsRes.value : parentsRes.value?.users ?? [])
    } catch { toast.error('Failed to load data') }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchData() }, [])

  const openLink = (student) => {
    setLinkModal(student)
    setLinkForm({ parentId: '', relation: 'father' })
  }

  const handleLink = async () => {
    if (!linkForm.parentId) { toast.error('Select a parent'); return }
    try {
      setLinking(true)
      const sid = linkModal._id || linkModal.id
      await relationsApi.link({ studentId: sid, parentId: linkForm.parentId, relation: linkForm.relation })
      setUnlinked((prev) => prev.filter((s) => (s._id || s.id) !== sid))
      toast.success('Parent linked successfully')
      setLinkModal(null)
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to link') }
    finally { setLinking(false) }
  }

  const filtered = unlinked.filter((s) =>
    getPersonName(s).toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Parent–Student Links</h2>
          <p className="text-sm text-gray-500 mt-0.5">{loading ? '...' : `${unlinked.length} unlinked students`}</p>
        </div>
        <button onClick={fetchData} className="btn-secondary p-2 self-start sm:self-auto"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="card p-4 flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-sm rounded-xl">
        <Link2 className="w-4 h-4 flex-shrink-0" />
        <span>These students don't have a parent linked yet. Link them to a parent for notifications and fee visibility.</span>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student..." className="bg-transparent text-sm outline-none w-full" />
        </div>
      </div>

      {/* Unlinked students */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? [...Array(6)].map((_, i) => (
          <div key={i} className="card p-4 animate-pulse space-y-3">
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full" />
              <div className="flex-1 space-y-2 pt-1"><div className="h-4 bg-gray-100 rounded w-2/3" /><div className="h-3 bg-gray-100 rounded w-1/2" /></div>
            </div>
            <div className="h-8 bg-gray-100 rounded" />
          </div>
        )) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">{unlinked.length === 0 ? 'All students are linked to parents!' : 'No students match your search.'}</p>
          </div>
        ) : filtered.map((s) => (
          <div key={s._id || s.id} className="card p-4">
            <div className="flex items-center gap-3 mb-4">
              <Avatar src={s.avatar} name={getPersonName(s)} size="md" />
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{getPersonName(s)}</p>
                <p className="text-xs text-gray-400 truncate">{getClassLabel(s)} · {s.admissionNo || s.rollNumber || ''}</p>
              </div>
            </div>
            <button onClick={() => openLink(s)} className="w-full btn-primary text-xs py-2 flex items-center justify-center gap-2">
              <Link2 className="w-3.5 h-3.5" /> Link Parent
            </button>
          </div>
        ))}
      </div>

      {/* Link Modal */}
      {linkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900">Link Parent</h3>
                <p className="text-xs text-gray-400 mt-0.5">{getPersonName(linkModal)}</p>
              </div>
              <button onClick={() => setLinkModal(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Parent *</label>
                <select className="input-field w-full" value={linkForm.parentId} onChange={(e) => setLinkForm({ ...linkForm, parentId: e.target.value })}>
                  <option value="">Select parent</option>
                  {parents.map((p) => (
                    <option key={p._id || p.id} value={p._id || p.id}>{getPersonName(p)} {p.email ? `(${p.email})` : ''}</option>
                  ))}
                </select>
                {parents.length === 0 && <p className="text-xs text-orange-500 mt-1">No parents found. Add parent users first.</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Relation</label>
                <select className="input-field w-full" value={linkForm.relation} onChange={(e) => setLinkForm({ ...linkForm, relation: e.target.value })}>
                  {relations.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setLinkModal(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleLink} disabled={linking} className="btn-primary flex items-center gap-2">
                {linking && <Loader2 className="w-4 h-4 animate-spin" />} Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
